import { useState, useCallback, useMemo } from 'react';
import { Container, Typography, Button, Stack, Paper, Dialog, DialogTitle, DialogContent, Box, Alert } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { queryItemCategories, queryItemTypes, ItemCategory } from '../schema';
import CategoryList from './CategoryList';
import CategoryForm from './CategoryForm';
import { createCategory, updateCategory, deleteCategory } from '../utils/categoryUtils';
import LoadingIndicator from './LoadingIndicator';
import { usePermissions } from '../hooks/usePermissions';
import PermissionDenied from './PermissionDenied';

interface CategoryFormValues {
    name: string;
    description: string;
    color: string;
}

export default function ItemCategoryManager() {
    const { canViewInventory, isLoading: permissionsLoading } = usePermissions();
    const { data: categoriesData, isLoading: categoriesLoading, error: categoriesError } = queryItemCategories();
    const { data: itemTypesData, isLoading: itemTypesLoading } = queryItemTypes();

    const [formDialogOpen, setFormDialogOpen] = useState(false);
    const [editingCategory, setEditingCategory] = useState<ItemCategory | undefined>(undefined);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [categoryToDelete, setCategoryToDelete] = useState<ItemCategory | undefined>(undefined);
    const [error, setError] = useState<string | null>(null);

    const sortedCategories = useMemo(() => {
        const categories = categoriesData?.itemCategories || [];
        return [...categories].sort((a, b) => (a.order || 0) - (b.order || 0));
    }, [categoriesData]);
    const itemTypes = useMemo(() => itemTypesData?.itemTypes || [], [itemTypesData]);

    const handleCreate = () => {
        setEditingCategory(undefined);
        setFormDialogOpen(true);
        setError(null);
    };

    const handleEdit = (category: ItemCategory) => {
        setEditingCategory(category);
        setFormDialogOpen(true);
        setError(null);
    };

    const handleDeleteClick = (category: ItemCategory) => {
        setCategoryToDelete(category);
        setDeleteDialogOpen(true);
        setError(null);
    };

    const handleFormSubmit = useCallback(
        async (values: CategoryFormValues) => {
            try {
                setError(null);
                if (editingCategory) {
                    // Update existing category
                    const maxOrder = Math.max(...sortedCategories.map(c => c.order || 0), 0);
                    await updateCategory(editingCategory.id, editingCategory, {
                        ...values,
                        order: editingCategory.order || maxOrder + 1,
                    });
                } else {
                    // Create new category
                    const maxOrder = Math.max(...sortedCategories.map(c => c.order || 0), 0);
                    await createCategory({
                        ...values,
                        order: maxOrder + 1,
                    });
                }
                setFormDialogOpen(false);
                setEditingCategory(undefined);
            } catch (err: any) {
                setError(err.message || 'Failed to save category');
            }
        },
        [editingCategory, sortedCategories]
    );

    const handleDeleteConfirm = useCallback(async () => {
        if (!categoryToDelete) return;

        try {
            setError(null);
            await deleteCategory(categoryToDelete, itemTypes);
            setDeleteDialogOpen(false);
            setCategoryToDelete(undefined);
        } catch (err: any) {
            setError(err.message || 'Failed to delete category');
        }
    }, [categoryToDelete, itemTypes, setError, setDeleteDialogOpen, setCategoryToDelete]);

    if (!permissionsLoading && !canViewInventory) {
        return <PermissionDenied requiredPermission="View Inventory" />;
    }

    const handleFormCancel = () => {
        setFormDialogOpen(false);
        setEditingCategory(undefined);
        setError(null);
    };

    const handleDeleteCancel = () => {
        setDeleteDialogOpen(false);
        setCategoryToDelete(undefined);
        setError(null);
    };

    if (categoriesLoading || itemTypesLoading) {
        return <LoadingIndicator message="Loading categories..." />;
    }

    if (categoriesError) {
        return (
            <Container>
                <Alert severity="error">Error loading categories: {categoriesError.message}</Alert>
            </Container>
        );
    }

    return (
        <Container maxWidth="md" sx={{ py: 3, minWidth: 0, maxWidth: '100%', boxSizing: 'border-box' }}>
            <Stack spacing={3}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h4">Item Categories</Typography>
                    <Button variant="contained" startIcon={<AddIcon />} onClick={handleCreate}>
                        Create Category
                    </Button>
                </Box>

                {error && (
                    <Alert severity="error" onClose={() => setError(null)}>
                        {error}
                    </Alert>
                )}

                <Paper sx={{ p: 2 }}>
                    {sortedCategories.length === 0 ? (
                        <Typography color="text.secondary" align="center" sx={{ py: 4 }}>
                            No categories yet. Create your first category to get started.
                        </Typography>
                    ) : (
                        <CategoryList
                            categories={sortedCategories}
                            onEdit={handleEdit}
                            onDelete={handleDeleteClick}
                            itemTypes={itemTypes}
                        />
                    )}
                </Paper>

                {/* Create/Edit Form Dialog */}
                <Dialog open={formDialogOpen} onClose={handleFormCancel} maxWidth="sm" fullWidth>
                    <DialogTitle>{editingCategory ? 'Edit Category' : 'Create Category'}</DialogTitle>
                    <DialogContent>
                        <CategoryForm category={editingCategory} onSubmit={handleFormSubmit} onCancel={handleFormCancel} />
                    </DialogContent>
                </Dialog>

                {/* Delete Confirmation Dialog */}
                <Dialog open={deleteDialogOpen} onClose={handleDeleteCancel}>
                    <DialogTitle>Delete Category</DialogTitle>
                    <DialogContent>
                        <Stack spacing={2}>
                            <Typography>
                                Are you sure you want to delete &quot;{categoryToDelete?.name}&quot;? This action cannot be undone.
                            </Typography>
                            {error && <Alert severity="error">{error}</Alert>}
                            <Stack direction="row" spacing={2} sx={{ justifyContent: 'flex-end', mt: 2 }}>
                                <Button onClick={handleDeleteCancel}>Cancel</Button>
                                <Button onClick={handleDeleteConfirm} color="error" variant="contained">
                                    Delete
                                </Button>
                            </Stack>
                        </Stack>
                    </DialogContent>
                </Dialog>
            </Stack>
        </Container>
    );
}
